import {
  AuthenticationResult,
  AuthError,
  EventMessage,
  EventType,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useSnackbar } from 'notistack';
import { PropsWithChildren, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { PERMISSIONS } from 'shared/constants';
import { useAuthState } from 'shared/stores/auth-state';
import { InsightB2CClaims } from 'shared/types';
import { getAppPermissions, getUserInfoFromIdClaims } from 'shared/utility';
import { CustomNavigationClient } from 'startup/custom-navigation-client';

type AuthProviderProps = {
  clientApp: IPublicClientApplication;
};

export function ProvideAuth({ children, clientApp }: PropsWithChildren<AuthProviderProps>) {
  const history = useHistory();
  const authState = useAuthState();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const navigationClient = new CustomNavigationClient(history);
    clientApp.setNavigationClient(navigationClient);

    const accounts = clientApp.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      clientApp.setActiveAccount(account);
      const user = getUserInfoFromIdClaims(account.idTokenClaims as InsightB2CClaims);
      authState.user.set(user);
    }

    clientApp.addEventCallback((event: EventMessage) => {
      if (event.payload) {
        switch (event.eventType) {
          case EventType.LOGIN_SUCCESS:
            const payload = event.payload as AuthenticationResult;
            // Verify Insight claims here.
            const account = payload.account;
            clientApp.setActiveAccount(account);
            const idTokenClaims = account?.idTokenClaims as InsightB2CClaims;
            const user = getUserInfoFromIdClaims(idTokenClaims);
            const appPermissions = getAppPermissions(user.insightClaims);
            if (!appPermissions.includes(PERMISSIONS.login)) {
              enqueueSnackbar(
                'Login Successful, but you do not have permission to access this application.\nlogging out now...',
                { variant: 'error' }
              );
              setTimeout(() => {
                clientApp.logout();
              }, 3500);
            }
            authState.user.set(user);
            break;
          case EventType.ACQUIRE_TOKEN_SUCCESS:
            const authResult = event.payload as AuthenticationResult;
            if (!authResult.fromCache) {
              // verify insight claims
              // console.log('New Token ', event.payload);
            }
            break;
          case EventType.LOGOUT_END:
            authState.user.set(null);
            break;
          case EventType.LOGIN_FAILURE:
          case EventType.ACQUIRE_TOKEN_FAILURE:
          case EventType.LOGOUT_FAILURE:
            const authError = event.error as AuthError;
            enqueueSnackbar(authError.message, { variant: 'error' });
            break;
          default:
            break;
        }
      }
    });
  }, [clientApp, history, authState, enqueueSnackbar]);

  return <MsalProvider instance={clientApp}>{children}</MsalProvider>;
}

export const AuthInProgress = () => <h1>Authorization in progress...</h1>;
